import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Calendar, Building2, Hammer, ArrowRight, DollarSign, Lock } from 'lucide-react'
import DataResultsContainer from './DataResultsContainer.tsx'
import styles from './ConversationalMode.module.scss'
import {Info} from 'lucide-react'   

interface WorkToQuote {
  id: string | number
  name: string
  description?: string
  location?: string | { id?: number; name?: string }
  organism?: string | { id?: number; name?: string }
  workType?: string | { id?: number; name?: string }
  amount?: number | string
  currency?: string
  endDate?: string
  createdAt?: string
  isPublic?: boolean
  canQuote?: boolean
  needsAccessRequest?: boolean
  projectType?: string
  rubros?: Array<{ name?: string }>
}

interface WorksToQuoteListProps {
  works: WorkToQuote[]
  total: number
  workVisibility: string
  isPublic?: boolean
  noResultsMessage?: string
}

const getStringValue = (value: string | { id?: number; name?: string } | undefined): string | undefined => {
  if (!value) return undefined
  if (typeof value === 'string') return value
  return value.name || undefined
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'Sin fecha'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'Sin fecha'
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatAmount = (amount?: number | string, currency?: string) => {
  if (!amount) return null
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numAmount) || numAmount === 0) return null
  return `${currency || '$'} ${numAmount.toLocaleString('es-AR')}`
}

const WorksToQuoteList: React.FC<WorksToQuoteListProps> = ({
  works,
  total,
  workVisibility,
  isPublic = false,
  noResultsMessage
}) => {
  const navigate = useNavigate()
  const typeLabel = isPublic ? 'públicas' : 'privadas'
  const publicNote = isPublic
    ? <div className={styles.publicNote}><Info size={12} /> Las obras públicas son informativas. Para cotizar, usá el sistema de compras de cada jurisdicción.</div>
    : undefined

  return (
    <DataResultsContainer
      icon={<Hammer size={18} />}
      title={`${works.length} obra${works.length !== 1 ? 's' : ''} ${typeLabel}`}
      subtitle={total > 3 ? `de ${total} disponibles` : undefined}
      color="blue"
      total={total}
      maxDisplay={3}
      viewAllLink={isPublic ? '/obras-publicas' : '/obras-privadas'}
      viewAllText={`Ver las ${total} obras ${typeLabel}`}
      emptyMessage={noResultsMessage || `No se encontraron obras ${typeLabel} disponibles.`}
      note={publicNote}
      isEmpty={works.length === 0}
    >
      {works.slice(0, 3).map((work) => {
        const locationStr = getStringValue(work.location)
        const organismStr = getStringValue(work.organism)
        const workTypeStr = getStringValue(work.workType)
        const amountStr = formatAmount(work.amount, work.currency)

        return (
          <div key={work.id} className={`${styles.resultCard} ${styles.resultCardBlue}`}>
            <div className={styles.resultCardHeader}>
              <span className={styles.resultCardTitle}>{work.name}</span>
              {isPublic && organismStr && (
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeBlue}`}>{organismStr}</span>
              )}
              {!isPublic && work.needsAccessRequest && (
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeBlue}`} title="Requiere solicitar acceso">
                  <Lock size={10} /> Acceso
                </span>
              )}
            </div>

            {work.description && (
              <p className={styles.resultCardDescription}>
                {work.description.length > 80
                  ? `${work.description.substring(0, 80)}...`
                  : work.description}
              </p>
            )}

            <div className={styles.resultCardMeta}>
              {locationStr && (
                <span><MapPin size={12} /> {locationStr}</span>
              )}
              {workTypeStr && (
                <span><Building2 size={12} /> {workTypeStr}</span>
              )}
              {amountStr && (
                <span><DollarSign size={12} /> {amountStr}</span>
              )}
              {work.endDate && (
                <span><Calendar size={12} /> Hasta {formatDate(work.endDate)}</span>
              )}
            </div>

            {!isPublic && work.rubros && work.rubros.length > 0 && (
              <div className={styles.resultCardChips}>
                {work.rubros.slice(0, 3).map((rubro, idx) => (
                  <span key={idx} className={styles.resultCardChip}>
                    {rubro.name || 'Rubro'}
                  </span>
                ))}
                {work.rubros.length > 3 && (
                  <span className={styles.resultCardChip}>+{work.rubros.length - 3}</span>
                )}
              </div>
            )}

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaBlue}`}
              onClick={() => {
                if (!isPublic) {
                  navigate(`/obrasprivadas/${work.id}`)
                } else if (work.projectType === 'Adjudicadas') {
                  navigate(`/obraspublicas/proyectos-adjudicados/${work.id}`)
                } else {
                  navigate(`/obra-publica-a-licitar/${work.id}`)
                }
              }}
            >
              {isPublic ? 'Ver detalle' : 'Ver obra y pedir acceso'} <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
    </DataResultsContainer>
  )
}

export default WorksToQuoteList

